// Quality (SQMS No. 1, F02/S22): the firm's system of quality management as `quality.status` publishes it — counts along
// objectives → risks → responses, monitoring and deficiencies, the engine's gaps, the last evaluation and the next one due.
// Every record act (objective, risk, response, monitoring, deficiency, remediation, evaluation) is a partner's own act.
import { Card, PageHeader, StatusPill } from "../../components";
import { fmtDate, titleCase } from "../../lib/format";
import { ActButton, Computed, ModelGate, Tiles, useActs, useFirmModels } from "./common";

type Doc = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
const CHAIN: Array<[string, string, string]> = [
  ["objectives", "Quality objectives", "quality.objective"], ["risks", "Quality risks", "quality.risk"], ["responses", "Responses", "quality.response"],
  ["monitoring", "Monitoring", "quality.monitor"], ["deficiencies", "Deficiencies", "quality.deficiency"], ["evaluations", "Evaluations", "quality.evaluate"],
];

export function QualityPage() {
  const ms = useFirmModels(["quality"]);
  const [act, drawer] = useActs();
  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader title="Quality" subtitle="SQMS No. 1: objectives, risks and responses, monitoring, deficiencies and the annual evaluation. Every record here is a partner's own act." />
      {ms.error && <p role="alert" className="text-sm text-blocked">{ms.error}</p>}
      {ms.data && (
        <ModelGate<Doc> kind="quality" m={ms.data.quality} label="The SQMS 1 record">
          {(d, m) => (
            <>
              <Tiles items={[
                { label: "Last evaluation", value: d.last_evaluation ? fmtDate(d.last_evaluation.evaluated_on) : "none", tone: d.last_evaluation ? "done" : "waiting" },
                { label: "Outcome", value: d.last_evaluation ? titleCase(String(d.last_evaluation.outcome)) : "—" },
                { label: "Next evaluation due", value: fmtDate(d.next_evaluation_due), tone: d.overdue ? "blocked" : undefined },
                { label: "Overdue", value: d.overdue ? "Yes" : "No", tone: d.overdue ? "blocked" : "done" },
                { label: "Gaps", value: (d.gaps ?? []).length, tone: (d.gaps ?? []).length ? "blocked" : "done" },
              ]} />
              <Card title="The record: objectives → risks → responses → monitoring" action={<Computed m={m} />}>
                <ol aria-label="SQMS 1 record" className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
                  {CHAIN.map(([k, label, cmd], i) => (
                    <li key={k} className="flex flex-col gap-2 rounded-md border border-line bg-sunken p-3">
                      <span className="text-xs text-muted">{i + 1}. {label}</span>
                      <span className="text-2xl font-semibold tabular-nums">{d.counts?.[k] ?? 0}</span>
                      <ActButton command={cmd} label={k === "evaluations" ? "Evaluate…" : "Record…"} tone={k === "evaluations" ? "primary" : undefined}
                        onClick={() => act({ command: cmd, title: k === "evaluations" ? "Record the annual evaluation" : `Record: ${label.toLowerCase().replace(/s$/, "")}` })} />
                    </li>
                  ))}
                </ol>
                <p className="mt-3 text-xs text-muted">The engine publishes the counts and the gaps; the entries themselves are read with <span className="font-mono">yvc quality status</span> on the engine's machine.</p>
              </Card>
              <Card title="Gaps the engine found">
                {(d.gaps ?? []).length === 0 ? <p className="text-sm text-muted">No gaps: every component has an objective, risks have responses, monitoring is recorded and deficiencies are remediated.</p> : (
                  <ul className="divide-y divide-line text-sm">
                    {(d.gaps as string[]).map((g) => (
                      <li key={g} className="flex flex-wrap items-start gap-2 py-2"><StatusPill state="blocked" label="Gap" /><span className="min-w-0 flex-1">{g}</span>
                        {/deficiency (\d+)/.test(g) && <ActButton command="quality.remediate" label="Remediate…" onClick={() => act({ command: "quality.remediate", fixed: { deficiency: Number(/deficiency (\d+)/.exec(g)![1]) }, title: "Record the remediation" })} />}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
              {d.last_evaluation && (
                <Card title="Last evaluation">
                  <p className="text-sm">{fmtDate(d.last_evaluation.evaluated_on)} by {d.last_evaluation.by ?? "—"}: <strong>{titleCase(String(d.last_evaluation.outcome))}</strong></p>
                  {d.last_evaluation.basis && <p className="mt-1 whitespace-pre-wrap text-sm text-muted">{d.last_evaluation.basis}</p>}
                </Card>
              )}
            </>
          )}
        </ModelGate>
      )}
      {drawer}
    </div>
  );
}
